import { useState } from 'react';
import RestaurantCard, { RestaurantCardSkeleton } from '@discover-components/restaurant/card';
import getRestaurantChips from '@discover-components/restaurant/card/getRestaurantChips';
import DiscountAvailabilitySection from '@discover-components/availability/discount/section';
import RegularAvailabilitySection from '@discover-components/availability/regular/section';
import RestaurantStoreDevMenu from '@discover-components/restaurant/devMenu';
import { useStoreState } from '@discover-data/stores/restaurant/hooks';
import shouldShowDiscountSection from '@discover-data/stores/restaurant/utils/discountAvailability/shouldShowDiscountSection';
import shouldShowRegularSection from '@discover-data/stores/restaurant/utils/regularAvailability/shouldShowRegularSection';
import useIsRowLayout from '@discover-components/availability/hooks/useIsRowLayout';
import useRestaurant from '@discover-data/stores/hooks/useRestaurant';
import useSession from '@discover-data/stores/hooks/session/useSession';
import { type PeopleOption } from '@discover-data/stores/hooks/people/options';
import withRestaurantStore from '@discover-data/stores/restaurant/withStore';
import { DiscountProvider } from '@/components/context/DiscountContext';
import RestaurantCardRoot, { type RestaurantCardRootProps } from './root';
import RestaurantCardContainer from './container';
import MapCloseButton from './closeButton';

export type MapCardProps = {
  id: number | null;
  onClose: () => void;
  peopleOptions?: PeopleOption[] | null;
  defaultSession?: SessionType;
} & Pick<RestaurantCardRootProps, 'className'>;

function MapCard({ id, onClose, className, peopleOptions, defaultSession }: MapCardProps) {
  const [processingBooking, setProcessingBooking] = useState(false);
  const { restaurant, loading } = useRestaurant({ id, peopleOptions, defaultSession });
  const [session] = useSession();
  const people = useStoreState((state) => state.people.value);
  const isRowLayout = useIsRowLayout();
  const showDiscountSection = useStoreState(
    (state) =>
      restaurant !== undefined &&
      shouldShowDiscountSection(
        state.regularOnly.value,
        state.byId.value,
        state.hasLastTable.value,
        state.discountAvailabilityById,
        state.effectiveDate,
        state.firebaseAvailabilitySessions,
        state.times.isSet,
        state.times.start,
        state.times.end,
        state.discountDatesToShowById,
        state.discountOnly.value,
        restaurant.id,
        state.mode === 'single',
        state.firebaseAllAvailability.loading,
        state.skipInitialFirebaseFetch,
        state.people.value
      )
  );
  const showRegularSection = useStoreState(
    (state) =>
      restaurant !== undefined &&
      shouldShowRegularSection(
        state.discountOnly.value,
        state.hasLastTable.value,
        state.byId.value,
        state.session.value,
        state.people.value,
        state.times.isSet,
        state.regularAvailabilityById,
        state.effectiveDate,
        state.mode === 'single',
        restaurant.id,
        showDiscountSection
      )
  );
  const visible = id !== null;

  if (id === null || (loading && restaurant === undefined)) {
    return (
      <>
        <RestaurantCardRoot visible={visible} className={className}>
          <RestaurantCardContainer>
            <MapCloseButton onClick={onClose} />
            <RestaurantCardSkeleton discount={0.5} className="m-3 mb-2" />
          </RestaurantCardContainer>
        </RestaurantCardRoot>
        <RestaurantStoreDevMenu />
      </>
    );
  }

  if (restaurant === undefined) {
    return (
      <>
        <RestaurantCardRoot visible={visible} className={className}>
          <RestaurantCardContainer>
            <MapCloseButton onClick={onClose} />
            <i className="mx-auto my-4 text-lg text-gray-400">Restaurant not found.</i>
          </RestaurantCardContainer>
        </RestaurantCardRoot>
        <RestaurantStoreDevMenu />
      </>
    );
  }

  let availabilitySections: JSX.Element[] = [];
  if (showDiscountSection) {
    availabilitySections = [
      <DiscountProvider key="discount-availability" defaultDiscount={0.5}>
        <DiscountAvailabilitySection
          restaurant={restaurant}
          processingBooking={processingBooking}
          setProcessingBooking={setProcessingBooking}
          showChips={false}
        />
      </DiscountProvider>,
    ];
  }

  // show 2 sections at most to not take up the whole screen
  if (showRegularSection && availabilitySections.length < 2) {
    availabilitySections.push(
      <RegularAvailabilitySection key="regular-availability" restaurant={restaurant} />
    );
  }

  return (
    <>
      <RestaurantCardRoot visible={visible} className={className}>
        <RestaurantCardContainer>
          <MapCloseButton onClick={onClose} />
          <RestaurantCard
            className="m-3 mb-2"
            restaurant={restaurant}
            restaurantChips={
              showDiscountSection
                ? getRestaurantChips(restaurant, session, people, isRowLayout)
                : undefined
            }
            availabilitySections={availabilitySections}
            session={session}
            people={people}
          />
        </RestaurantCardContainer>
      </RestaurantCardRoot>
      <RestaurantStoreDevMenu />
    </>
  );
}

export default withRestaurantStore(MapCard, 'Map Card Store');
